<script module>
    // Key (id + finishAt) of the event whose end has already triggered the
    // get-packs refresh below. Module-level on purpose: getStorePacks(true) swaps
    // the shop for the Loader and remounts this view, so a component-local flag
    // reset with every mount. When the response still carried the finished event
    // (client clock ahead of the server, a cached payload) the fresh mount refreshed
    // again, about once a second: the reward tiles were destroyed before tippy's
    // 500ms show delay elapsed, so no tooltip ever appeared, and taps on mobile were
    // lost with the row they landed on.
    let expiredRefreshedFor = null;
</script>

<script>
    import {_, number} from 'svelte-i18n';
    import {getContext, onMount} from 'svelte';
    import {chainPromoEvent, storePacks, getStorePacks, isMobile, endExpiredSession, isSessionExpired} from '../../stores';
    import {claim_gold_rush_route} from '../../constants.js';
    import Loader from '../Loader.svelte';
    import {resourceDetails} from '../../globals.js';
    import {goTo} from '../../utils.js';
    import ItemsListing from '../ItemsListing.svelte';
    import BottomSheet from '../BottomSheet.svelte';
    import PackDetailSheet from '../PackDetailSheet.svelte';
    import coinsArt from '../../assets/chain_deals/coins.png?format=webp;png';
    import arrowsArt from '../../assets/chain_deals/arrows.png?format=webp;png';
    import sparkleArt from '../../assets/chain_deals/sparkle.png?format=webp;png';
    import lockIcon from '../../assets/chain_deals/lock.png?format=webp;png';
    import infoIcon from '../../assets/gold_rush/info_icon.png?format=webp;png';

    let { globals = {} } = $props();

    // svelte-ignore state_referenced_locally
    globals.resourceDetails = resourceDetails;
    // svelte-ignore state_referenced_locally
    globals.formatTimeFriendly = formatTimeFriendly;

    function formatTimeFriendly(seconds) {
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);

        let timeString = `${minutes} ${$_('Minute')}`;

        if (seconds - 1 > 60 * 60 * 24) {
            timeString = `${days} ${$_('Days')}`;
        } else if (seconds > 60 * 60) {
            timeString = `${hours} ${$_('Hours')}`;
        } else if (seconds >= 60) {
            timeString = `${minutes} ${$_('Minutes')}`;
        }

        return timeString;
    }

    const {buyPack: buyPackFromContext} = getContext('buyPack');

    let processing = $state(false);
    let claimingId = $state(null); // prizeId whose Claim button is busy
    let celebrateId = $state(null); // row bouncing after a successful claim (same rubberBand as the daily free pack)
    let unlockedId = $state(null);  // row pulsing because it just became claimable
    let errorMessage = $state('');
    let now = $state(Date.now());
    let showHowTo = $state(false);

    // Mobile detail sheets: a free step opens the reward sheet, a purchase step
    // opens the shop's pack sheet (with the embedded checkout).
    let detailStep = $state(null);
    let sheetPack = $state(null);

    onMount(() => {
        window.scrollTo(0, 0);
        goTo('/supplies/specialEvents?tab=chainDeals', true);
        const timer = setInterval(() => {
            now = Date.now();

            // Once the event ends, one forced refresh drops chainPromoEvent and the
            // tab. If the event comes back anyway, the ladder simply stays up (the
            // time bar reads 00:00:00) until the next natural get-packs.
            const event = $chainPromoEvent;
            const finishAt = event?.finishAt && new Date(event.finishAt).getTime();
            const key = finishAt ? `${event.id}:${event.finishAt}` : null;
            if (key && finishAt <= now && expiredRefreshedFor !== key) {
                expiredRefreshedFor = key;
                getStorePacks(true);
            }
        }, 1000);
        return () => clearInterval(timer);
    });

    let steps = $derived(
        Object.entries($chainPromoEvent?.prizes ?? {})
            .map(([key, prize]) => ({
                key,
                prizeId: parseInt(key.replace('prize', ''), 10),
                ...prize,
                claimEntry: $chainPromoEvent?.claimablePrizes?.[key],
            }))
            .sort((a, b) => a.pointsNeeded - b.pointsNeeded)
    );

    function stepState(step) {
        if (step.claimEntry?.claimed) return 'collected';
        if (step.claimEntry) return 'current';
        return 'locked';
    }

    // Same hh:mm:ss readout as the in-game footer ("Time left: 07:18:57").
    let timeLeft = $derived.by(() => {
        if (!$chainPromoEvent?.finishAt) return '';
        let seconds = Math.max(0, Math.floor((new Date($chainPromoEvent.finishAt).getTime() - now) / 1000));
        const days = Math.floor(seconds / 86400);
        const pad = (n) => String(n).padStart(2, '0');
        const clock = `${pad(Math.floor((seconds % 86400) / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
        return days > 0 ? `${days}d ${clock}` : clock;
    });

    function packFor(step) {
        return ($storePacks || []).find((p) => p.internalId === step.packId);
    }

    function priceString(step) {
        const pack = packFor(step);
        if (!pack?.paymentHub?.price || pack.paymentHub.price.amount === 'missing' || pack.paymentHub.price.currency === 'missing') return '';
        return $number(pack.paymentHub.price.amount / 100, {
            style: 'currency',
            currency: pack.paymentHub.price.currency,
        });
    }

    // The checkout and pack sheet use the game's own event strings.
    function packDetailsFor() {
        const title = $_('ChainDealTitle');
        return {
            title,
            description: $_('ChainDealDescription'),
            subTitle: title,
        };
    }

    // Same feedback as collecting a daily free pack: the claimed row bounces, then
    // the row that just opened pulses once so the eye moves on to it.
    function celebrate(step) {
        celebrateId = step.prizeId;
        setTimeout(() => { if (celebrateId === step.prizeId) celebrateId = null; }, 1000);
        const nextId = step.prizeId + 1;
        if (steps.some((s) => s.prizeId === nextId)) {
            setTimeout(() => {
                unlockedId = nextId;
                setTimeout(() => { if (unlockedId === nextId) unlockedId = null; }, 1000);
            }, 350);
        }
    }

    // Mirror what the server does on a successful claim: mark the prize claimed,
    // add one point and open the prize that now meets the points threshold.
    function advanceChainLocally(step) {
        chainPromoEvent.update((event) => {
            if (!event?.prizes) return event;
            const claimablePrizes = {...(event.claimablePrizes ?? {})};
            claimablePrizes[step.key] = {...(claimablePrizes[step.key] ?? {}), claimed: 1};
            const points = (event.points ?? 0) + 1;
            const nextKey = Object.entries(event.prizes)
                .find(([key, prize]) => prize.pointsNeeded === points && !claimablePrizes[key])?.[0];
            if (nextKey) claimablePrizes[nextKey] = {claimed: 0, seasonPassClaimed: 0};
            return {...event, points, claimablePrizes};
        });
    }

    async function claimFreeStep(step) {
        if (processing) return;
        try {
            processing = true;
            claimingId = step.prizeId;
            errorMessage = '';
            const response = await fetch(claim_gold_rush_route, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    prizeId: step.prizeId,
                    eventId: $chainPromoEvent.id,
                }),
            });

            // A 403 is web-shop's LoggedInGuard (see GoldRushPack.svelte): the session is gone, not
            // the reward claimed. Clear it so <Login/> takes over instead of reporting it.
            if (isSessionExpired(response)) {
                endExpiredSession(claim_gold_rush_route);
                return;
            }

            if (!response.ok) {
                errorMessage = $_('PleaseTryAgainLater');
                Rollbar.error('Chain deal claim failed: ' + response.status);
                return;
            }

            // A 2xx means the server granted the items, added the point and opened
            // the next prize inside one transaction, so the local update below is
            // exact and no follow-up get-packs is needed.
            advanceChainLocally(step);
            detailStep = null;
            celebrate(step);
        } catch (error) {
            Rollbar.error('Chain deal claim error: ' + error);
            errorMessage = $_('PleaseTryAgainLater');
        } finally {
            processing = false;
            claimingId = null;
        }
    }

    function buyStep(step) {
        const storePack = packFor(step);
        if (!storePack) return;
        buyPackFromContext({
            detail: {
                storePack,
                packDetails: packDetailsFor(),
            },
        });
    }

    // Primary action of a step (the CTA button).
    function act(step) {
        if (stepState(step) !== 'current') return;
        if (step.unlockedBy === 'purchase') {
            if ($isMobile) openStep(step);
            else buyStep(step);
        } else {
            claimFreeStep(step);
        }
    }

    // Tapping the body of a step on mobile shows its full reward list.
    function openStep(step) {
        if (!$isMobile) return;
        if (step.unlockedBy === 'purchase') {
            const storePack = packFor(step);
            if (!storePack) return;
            sheetPack = {
                storePack,
                storeSection: 'chainDeals',
                packDetails: packDetailsFor(),
                items: step.items,
                goldAmount: 0,
                goldWorth: 0,
                priceString: stepState(step) === 'current' ? priceString(step) : '',
                imageSrc: null,
                globals,
                rarityClass: 'legendary',
                badge: step.bonusAmount ? {label: 'Bonus', text: step.bonusAmount} : null,
            };
        } else {
            detailStep = step;
        }
    }

    function onSheetBuy() {
        // Gift flow only; chain packs never take this path.
    }
</script>

{#snippet lock()}
    <picture class="cd-lock" aria-hidden="true">
        <source srcset={lockIcon[0]} type="image/webp">
        <source srcset={lockIcon[1]} type="image/png">
        <img alt="" src={lockIcon[1]}>
    </picture>
{/snippet}

<section class="chain-deals" aria-labelledby="chain-deals-title">
    <!-- Banner: the in-game "Soldier's Spoils" header — green band, gold title, event icon. -->
    <header class="ss-banner">
        <div class="ss-fx" aria-hidden="true">
            <picture class="ss-arrows a1">
                <source srcset={arrowsArt[0]} type="image/webp">
                <source srcset={arrowsArt[1]} type="image/png">
                <img alt="" src={arrowsArt[1]}>
            </picture>
            <picture class="ss-arrows a2">
                <source srcset={arrowsArt[0]} type="image/webp">
                <source srcset={arrowsArt[1]} type="image/png">
                <img alt="" src={arrowsArt[1]}>
            </picture>
            {#each [1, 2, 3, 4, 5, 6] as n (n)}
                <picture class="ss-spark s{n}">
                    <source srcset={sparkleArt[0]} type="image/webp">
                    <source srcset={sparkleArt[1]} type="image/png">
                    <img alt="" src={sparkleArt[1]}>
                </picture>
            {/each}
            <picture class="ss-coins">
                <source srcset={coinsArt[0]} type="image/webp">
                <source srcset={coinsArt[1]} type="image/png">
                <img alt="" src={coinsArt[1]}>
            </picture>
        </div>
        <h2 id="chain-deals-title" class="ss-title">{$_('ChainDealTitle')}</h2>
        <button type="button" class="ss-info" aria-label={$_('KvkHowItWorksButton')}
                aria-expanded={showHowTo} aria-controls="ss-howto"
                onclick={() => (showHowTo = !showHowTo)}>
            <picture>
                <source srcset={infoIcon[0]} type="image/webp">
                <source srcset={infoIcon[1]} type="image/png">
                <img alt="" src={infoIcon[1]}>
            </picture>
        </button>
    </header>
    <p class="ss-strip">{$_('ChainDealDescription')}</p>
    <div id="ss-howto" class="ss-howto" hidden={!showHowTo}>
        <strong>{$_('KvkHowItWorksButton')}</strong>
        <p>{$_('ChainDealHowItWorksDescription')}</p>
    </div>

    {#if errorMessage}
        <p class="cd-error" role="alert">{errorMessage}</p>
    {/if}

    <ol class="cd-chain">
        {#each steps as step, index (step.key)}
            {@const state = stepState(step)}
            {@const purchase = step.unlockedBy === 'purchase'}
            {@const price = purchase ? priceString(step) : ''}
            <li class="cd-step {state}" class:purchase class:free={!purchase} class:last={index === steps.length - 1}
                aria-current={state === 'current' ? 'step' : undefined}>
                <article class="cd-card animate__animated"
                         class:animate__rubberBand={celebrateId === step.prizeId}
                         class:animate__pulse={unlockedId === step.prizeId}>
                    {#if state !== 'collected'}
                        <span class="cd-ribbon" class:more={purchase} aria-hidden="true">
                            {#if purchase}<b>{step.bonusAmount}</b> {$_('More')}{:else}{$_('Free')}{/if}
                        </span>
                    {/if}

                    {#if $isMobile}
                        <button type="button" class="cd-body" onclick={() => openStep(step)}
                                aria-label={purchase ? `${step.bonusAmount} ${$_('More')}` : $_('Free')}>
                            <div class="cd-items">
                                <ItemsListing storePack={{}} goldAmount={0} {globals} items={step.items} hasMask={false} areRewards={false}/>
                            </div>
                        </button>
                    {:else}
                        <div class="cd-body">
                            <div class="cd-items">
                                <ItemsListing storePack={{}} goldAmount={0} {globals} items={step.items} hasMask={false} areRewards={false}/>
                            </div>
                        </div>
                    {/if}

                    <div class="cd-action">
                        {#if state === 'collected'}
                            <span class="cd-cta claimed">{$_('EventClaimedButton')}</span>
                        {:else if state === 'current' && purchase}
                            <button type="button" class="cd-cta plate" disabled={processing || !packFor(step)} onclick={() => act(step)}>
                                {price || $_('AMAttemptsModalBuy')}
                            </button>
                        {:else if state === 'current'}
                            <button type="button" class="cd-cta claim" class:busy={claimingId === step.prizeId}
                                    disabled={processing} aria-busy={claimingId === step.prizeId} onclick={() => act(step)}>
                                {#if claimingId === step.prizeId}
                                    <span class="cd-spin" aria-hidden="true"><Loader size="12" color="#0f3a06" duration="0.9s"/></span>
                                    <span class="cd-visually-hidden">{$_('EventClaimButton')}</span>
                                {:else}
                                    {$_('EventClaimButton')}
                                {/if}
                            </button>
                        {:else}
                            <span class="cd-cta is-locked" class:plate={purchase} class:claim={!purchase}
                                  role="img" aria-label="{$_('Locked')}: {purchase ? (price || $_('AMAttemptsModalBuy')) : $_('EventClaimButton')}">
                                <span class="cd-cta-label">{purchase ? (price || $_('AMAttemptsModalBuy')) : $_('EventClaimButton')}</span>
                                {@render lock()}
                            </span>
                        {/if}
                    </div>
                </article>
            </li>
        {/each}
    </ol>

    {#if timeLeft}
        <footer class="ss-timebar">{$_('TimeLeft')}: <time>{timeLeft}</time></footer>
    {/if}
</section>

{#if $isMobile}
    <BottomSheet
        open={!!detailStep}
        onClose={() => (detailStep = null)}
        title={detailStep ? $_('Free') : ''}
    >
        {#if detailStep}
            <div class="cd-sheet">
                <div class="cd-sheet-band">
                    <p class="cd-sheet-title">{$_('Free')}</p>
                    <p class="cd-sheet-step">{detailStep.prizeId} / {steps.length}</p>
                </div>
                <div class="cd-sheet-items">
                    <ItemsListing storePack={{}} goldAmount={0} {globals} items={detailStep.items} hasMask={false}/>
                </div>
            </div>
        {/if}

        {#snippet footer()}
            {#if detailStep}
                {@const state = stepState(detailStep)}
                {#if state === 'current'}
                    <button type="button" class="cd-sheet-cta" disabled={processing} aria-busy={claimingId === detailStep.prizeId}
                            onclick={() => claimFreeStep(detailStep)}>
                        {#if claimingId === detailStep.prizeId}
                            <span class="cd-spin" aria-hidden="true"><Loader size="12" color="#0f3a06" duration="0.9s"/></span>
                        {:else}
                            {$_('EventClaimButton')}
                        {/if}
                    </button>
                {:else if state === 'collected'}
                    <p class="cd-sheet-note">{$_('EventClaimedButton')}</p>
                {:else}
                    <p class="cd-sheet-note">{@render lock()} {$_('Locked')}</p>
                {/if}
            {/if}
        {/snippet}
    </BottomSheet>

    <PackDetailSheet pack={sheetPack} onClose={() => (sheetPack = null)} onBuy={onSheetBuy}/>
{/if}

<style lang="scss">
    @use 'sass:color';
    @use '../../styles/breakpoints.scss' as bp;
    @use '../../styles/vars.scss' as *;

    // Palette lifted from the in-game Soldier's Spoils screen.
    $banner-green-1: #2f9d3f;
    $banner-green-2: #0c4a1c;
    $title-gold-1: #ffe066;
    $title-gold-2: #f2a71b;
    $strip-yellow-1: #f6cf2c;
    $strip-yellow-2: #e0a70b;
    $free-1: #d9b8f2;          // free-step body (light lavender)
    $free-2: #b98adf;
    $free-locked-1: #c7acdb;
    $free-locked-2: #a98ac2;
    $claimed-1: #6a3d8f;       // claimed-step body (deep purple)
    $claimed-2: #4b2a68;
    $deal-1: #ffd977;          // purchase-step body (orange gold)
    $deal-2: #f2a52b;
    $deal-locked-1: #e9cc84;
    $deal-locked-2: #d59a3e;
    $ribbon-free-1: #ffd94a;
    $ribbon-free-2: #eaa50a;
    $ribbon-more-1: #ee4b3a;
    $ribbon-more-2: #b41f16;
    $claim-green-1: #a8e563;
    $claim-green-2: #5cb51a;
    $ink: #2b1440;
    $ink-deal: #3a1f02;

    .chain-deals {
        max-width: 760px;
        margin: 0 auto;
        padding: 0 14px 24px;
        color: #fff;
        font-family: var(--font-family-sans-condensed);

        @include bp.mobile {
            padding: 0 10px 12px;
        }
    }

    // ── Banner ──────────────────────────────────────────────────────────────
    .ss-banner {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 112px;
        padding: 14px 64px;
        border-radius: 14px 14px 0 0;
        background:
            radial-gradient(ellipse at 50% 35%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0) 55%),
            linear-gradient(180deg, $banner-green-1 0%, $banner-green-2 100%);
        box-shadow: inset 0 -6px 14px rgba(0, 0, 0, 0.35);
        overflow: hidden;

        @include bp.mobile {
            min-height: 96px;
            padding: 12px 74px 12px 50px; // coins live in the right gutter
        }
    }

    .ss-title {
        position: relative;
        z-index: 1;
        margin: 0;
        font-family: var(--font-family-sans-condensed);
        font-weight: 900;
        font-size: clamp(22px, 6.4vw, 44px);
        line-height: 1;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        text-align: center;
        color: $title-gold-1;
        background: linear-gradient(180deg, $title-gold-1 30%, $title-gold-2 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 2px 0 #4a2a05) drop-shadow(0 3px 4px rgba(0, 0, 0, 0.55));
    }

    // Decorative layer: the game's rising green arrows on the left, tumbling
    // coins on the right and a few twinkling sparkles. Purely ornamental.
    .ss-fx {
        position: absolute;
        inset: 0;
        pointer-events: none;
        overflow: hidden;

        picture, img { display: block; }
    }

    .ss-arrows {
        position: absolute;
        bottom: 6px;
        width: 64px;
        opacity: 0;
        filter: drop-shadow(0 0 6px rgba(140, 255, 120, 0.7));
        animation: ss-rise 5s ease-out infinite;

        img { width: 100%; }

        &.a1 { left: 12px; }
        &.a2 { left: 22px; width: 44px; bottom: 10px; animation-delay: 1.1s; }

        @include bp.mobile {
            width: 52px;
            &.a2 { left: 20px; width: 36px; }
        }
    }

    @keyframes ss-rise {
        0%   { transform: translateY(26px); opacity: 0; }
        10%  { opacity: 1; }
        34%  { transform: translateY(-48px); opacity: 0; }
        100% { transform: translateY(-48px); opacity: 0; }
    }

    .ss-coins {
        position: absolute;
        right: 4px;
        top: -4px;
        width: 118px;
        filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.45));

        img { width: 100%; }

        @include bp.mobile {
            width: 82px;
            right: -4px;
            top: auto;
            bottom: -10px;
        }
    }

    .ss-spark {
        position: absolute;
        width: 22px;
        opacity: 0;
        animation: ss-twinkle 3.6s ease-in-out infinite;

        img { width: 100%; }

        &.s1 { left: 14%; top: 18%; animation-delay: 0s; }
        &.s2 { left: 27%; top: 62%; width: 14px; animation-delay: 0.9s; }
        &.s3 { left: 48%; top: 8%;  width: 16px; animation-delay: 1.7s; }
        &.s4 { left: 64%; top: 70%; width: 18px; animation-delay: 2.4s; }
        &.s5 { left: 78%; top: 14%; width: 14px; animation-delay: 0.5s; }
        &.s6 { left: 90%; top: 58%; width: 20px; animation-delay: 1.3s; }
    }

    @keyframes ss-twinkle {
        0%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); }
        50%      { opacity: 0.95; transform: scale(1) rotate(25deg); }
    }

    @media (prefers-reduced-motion: reduce) {
        .ss-arrows { animation: none; opacity: 0.85; transform: none; }
        .ss-spark { animation: none; opacity: 0.6; transform: none; }
    }

    .ss-info {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 40px;
        height: 40px;
        padding: 6px;
        border: none;
        border-radius: 50%;
        background: transparent;
        cursor: pointer;

        img {
            width: 100%;
            display: block;
        }

        &:focus-visible {
            outline: 3px solid #fff;
            outline-offset: 2px;
        }
    }

    // Yellow message strip under the banner.
    .ss-strip {
        margin: 0;
        padding: 10px 14px;
        text-align: center;
        font-size: 16px;
        font-weight: 800;
        color: #4a2a05;
        background: linear-gradient(180deg, $strip-yellow-1, $strip-yellow-2);
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.35);
        // Slightly torn edges like the painted band in the game.
        clip-path: polygon(0 4%, 3% 0, 9% 5%, 17% 1%, 26% 6%, 35% 0, 44% 5%, 53% 1%, 62% 6%, 71% 0, 80% 5%, 89% 1%, 97% 5%, 100% 0,
            100% 96%, 96% 100%, 88% 95%, 79% 100%, 70% 94%, 61% 100%, 52% 95%, 43% 100%, 34% 94%, 25% 100%, 16% 95%, 8% 100%, 0 96%);
    }

    .ss-howto {
        margin: 12px 0 0;
        padding: 12px 14px;
        border-radius: 10px;
        background: rgba(0, 0, 0, 0.35);
        border: 1px solid rgba(255, 214, 140, 0.25);
        color: #ffefd0;
        font-size: 14px;
        line-height: 1.4;

        strong {
            display: block;
            margin-bottom: 4px;
            font-size: 15px;
        }
        p {
            margin: 0;
            white-space: pre-line;
        }
    }

    .cd-error {
        margin: 12px 0 0;
        padding: 8px 12px;
        border-radius: 8px;
        background: rgba(223, 27, 65, 0.18);
        border: 1px solid rgba(223, 27, 65, 0.45);
        color: #ffd7de;
        font-size: 14px;
    }

    // ── Steps ───────────────────────────────────────────────────────────────
    .cd-chain {
        list-style: none;
        margin: 14px 0 0;
        padding: 0;
    }

    .cd-step {
        position: relative;
        padding-bottom: 20px;

        // Dark rail between rows with a small notch at the centre, like the
        // strip that links the rows in the game.
        &::after {
            content: '';
            position: absolute;
            left: 14px;
            right: 14px;
            bottom: 5px;
            height: 10px;
            background: rgba(0, 0, 0, 0.42);
            clip-path: polygon(0 0, 100% 0, 100% 40%, 52.5% 40%, 50% 100%, 47.5% 40%, 0 40%);
        }
        &.last {
            padding-bottom: 0;
            &::after { display: none; }
        }
    }

    .cd-card {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: 12px;
        min-height: 92px;
        padding: 14px 14px 14px 46px; // the corner ribbon lives in this gutter, clear of the first tile
        border-radius: 12px;
        overflow: hidden; // clips the corner ribbon
        color: $ink;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.35);
        transition: background 0.35s ease, box-shadow 0.35s ease, color 0.35s ease, opacity 0.6s ease;

        // Claimed rows recede so the open and locked steps stand out.
        .collected & {
            opacity: 0.6;
        }

        .free & { background: linear-gradient(180deg, $free-1, $free-2); }
        .free.locked & { background: linear-gradient(180deg, $free-locked-1, $free-locked-2); }
        .free.collected & { background: linear-gradient(180deg, $claimed-1, $claimed-2); color: #fff; }

        .purchase & { background: linear-gradient(180deg, $deal-1, $deal-2); color: $ink-deal; }
        .purchase.locked & { background: linear-gradient(180deg, $deal-locked-1, $deal-locked-2); }
        .purchase.collected & { background: linear-gradient(180deg, color.adjust($deal-2, $lightness: -12%), color.adjust($deal-2, $lightness: -26%)); color: #fff; }

        // The live step gets the cream ring (same as the active nav pill).
        .current & {
            box-shadow: 0 0 0 3px #e2e29b, 0 8px 22px rgba(0, 0, 0, 0.45);
        }

        @include bp.mobile {
            padding: 12px 12px 12px 44px;
            gap: 10px;
        }
    }

    // Corner ribbon: a long band rotated through the corner so only its middle
    // shows; kept small enough to sit in the card's left gutter, clear of the
    // first tile. The bonus ribbon stacks "+1200%" over "MORE" like the game's.
    .cd-ribbon {
        position: absolute;
        top: 12px;
        left: -47px;
        width: 150px;
        padding: 3px 0;
        transform: rotate(-45deg);
        text-align: center;
        text-transform: uppercase;
        font-size: 10px;
        font-weight: 900;
        line-height: 1.05;
        letter-spacing: 0.02em;
        color: #3a2205;
        background: linear-gradient(180deg, $ribbon-free-1, $ribbon-free-2);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
        z-index: 2;
        pointer-events: none;
        overflow: hidden;

        &.more {
            color: #fff;
            background: linear-gradient(180deg, $ribbon-more-1, $ribbon-more-2);
            text-shadow: 0 1px 1px rgba(0, 0, 0, 0.4);
            font-size: 8px;
            line-height: 1.1;

            b {
                display: block;
                font-size: 10px;
            }
        }

        // A light sweep runs along the band every 15s (bottom-left to top-right
        // on screen once the band is rotated), like the game's badge shine.
        &::after {
            content: '';
            position: absolute;
            top: -20%;
            bottom: -20%;
            left: 0;
            width: 34%;
            background: linear-gradient(100deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0) 100%);
            transform: translateX(-140%) skewX(-12deg);
            animation: cd-shine 15s linear infinite;
        }
    }

    @keyframes cd-shine {
        0%   { transform: translateX(-140%) skewX(-12deg); }
        12%  { transform: translateX(420%) skewX(-12deg); }
        100% { transform: translateX(420%) skewX(-12deg); }
    }

    @media (prefers-reduced-motion: reduce) {
        .cd-ribbon::after { animation: none; display: none; }
    }

    .cd-body {
        display: block;
        min-width: 0;
        margin: 0;
        padding: 0;
        border: none;
        background: transparent;
        color: inherit;
        text-align: left;
        font: inherit;
        cursor: default;
        border-radius: 8px;

        &:is(button) {
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
        }
    }

    .cd-spin {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 3.2em;
        height: 1em;

        :global(.loader) {
            display: flex;
            gap: 4px;
            width: auto;
            height: auto;
        }
    }

    .cd-visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }

    .cd-body:focus-visible,
    .cd-cta:focus-visible,
    .cd-sheet-cta:focus-visible,
    .ss-info:focus-visible {
        outline: 3px solid #fff;
        outline-offset: 2px;
    }

    // Reward icons: one row of tiles with the count badge inside each tile.
    .cd-items {
        :global(.rewards) {
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            width: 100%;
        }
        :global(.rewards > div) {
            display: flex !important;
            flex-wrap: nowrap;
            gap: 10px;
            height: auto !important;
            max-height: none !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            padding: 2px 0 !important;
            mask-image: none !important;
            -webkit-mask-image: none !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            &::-webkit-scrollbar { display: none; }
        }
        :global(.item) {
            display: block !important;
            width: auto !important;
            height: auto !important;
            min-width: 0 !important;
            padding: 0 !important;
            border: none !important;
            position: relative;
            flex: 0 0 auto;
        }
        :global(.item .item-title) {
            display: none !important;
        }
        :global(.item .item-icon) {
            width: 56px !important;
            height: 56px !important;
            max-width: 56px !important;
            max-height: 56px !important;
            border-radius: 8px;
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.55), 0 2px 4px rgba(0, 0, 0, 0.35);

            @include bp.mobile {
                width: 50px !important;
                height: 50px !important;
                max-width: 50px !important;
                max-height: 50px !important;
            }
        }
        :global(.item .item-amount) {
            position: absolute !important;
            top: auto !important;
            left: auto !important;
            right: 3px !important;
            bottom: 3px !important;
            z-index: 2;
            display: inline-block !important;
            width: auto !important;
            min-width: 20px !important;
            height: 18px !important;
            margin: 0 !important;
            padding: 0 5px !important;
            order: 0 !important;
            border: none;
            border-radius: 6px;
            background: rgba(10, 5, 2, 0.82);
            box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
            color: #fff !important;
            font-size: 12px !important;
            font-weight: 800;
            line-height: 18px !important;
            text-align: center;
            text-shadow: none;
            --amt-base: 12px;
        }
        :global(.item .greenCheckmark) {
            display: none !important;
        }

        .locked & :global(.item-icon) {
            filter: saturate(0.5) brightness(0.92);
        }
    }

    // ── Actions ─────────────────────────────────────────────────────────────
    .cd-action {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    .cd-cta {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 48px;
        min-width: 116px;
        padding: 0 18px;
        border: none;
        border-radius: 10px;
        font-family: inherit;
        font-size: 18px;
        font-weight: 800;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);

        @include bp.mobile {
            min-width: 104px;
            padding: 0 14px;
            font-size: 17px;
        }

        &:disabled {
            cursor: default;
            opacity: 0.7;
        }
        // While its own claim is in flight the button stays fully lit, only the
        // label swaps for the spinner.
        &.busy:disabled {
            opacity: 1;
            cursor: progress;
        }

        // Green claim button (dark text keeps the contrast the game's white text lacks).
        &.claim {
            color: #0f3a06;
            background: radial-gradient(ellipse at center, $claim-green-1 10%, $claim-green-2 100%);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3),
                inset 0 2px 0 0 rgba(255, 255, 255, 0.45),
                inset 0 -2px 1px 1px rgba(0, 0, 0, 0.3);
        }

        // The shop's orange price plate.
        &.plate {
            color: rgba(0, 0, 0, 0.82);
            background: radial-gradient(ellipse at center, #ffcc00 10%, #ff7700 100%);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3),
                inset 0 2px 0 0 rgba(255, 255, 255, 0.45),
                inset 0 -2px 1px 1px rgba(0, 0, 0, 0.3);
            font-weight: 900;
        }

        // Claimed: greyed-out pill, like the game.
        &.claimed {
            cursor: default;
            color: #fff;
            background: linear-gradient(180deg, #8f889b, #6a6373);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 2px 4px rgba(0, 0, 0, 0.3);
            text-shadow: 0 1px 1px rgba(0, 0, 0, 0.35);
        }

        // Locked: the button is shown muted with the gold padlock over its corner.
        &.is-locked {
            cursor: default;
            opacity: 0.85;
            filter: saturate(0.55);

            .cd-cta-label {
                opacity: 0.75;
            }
        }
    }

    .cd-lock {
        position: absolute;
        right: -10px;
        bottom: -10px;
        width: 38px;
        pointer-events: none;

        img {
            width: 100%;
            display: block;
            filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.5));
        }
    }

    // ── Time bar (sticks to the bottom on phones, like the game footer) ─────
    .ss-timebar {
        margin: 6px 0 0;
        padding: 10px 14px;
        text-align: center;
        font-size: 17px;
        font-weight: 800;
        color: #4a2a05;
        background: linear-gradient(180deg, $strip-yellow-1, $strip-yellow-2);
        border-radius: 0 0 14px 14px;
        box-shadow: 0 -3px 8px rgba(0, 0, 0, 0.35);

        time {
            font-variant-numeric: tabular-nums;
        }

        @include bp.mobile {
            position: sticky;
            bottom: 0;
            z-index: 8;
            border-radius: 10px;
            margin-top: 10px;
            padding-bottom: calc(10px + bp.$safe-bottom);
        }
    }

    // ── Free-reward sheet (mobile) ──────────────────────────────────────────
    .cd-sheet {
        background: #f3e4bf;
        color: $ink-deal;
        border-radius: 14px;
        overflow: hidden;
    }

    .cd-sheet-band {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        padding: 12px 14px;
        background: linear-gradient(180deg, $free-1, $free-2);
        color: $ink;

        .cd-sheet-title {
            margin: 0;
            font-size: 22px;
            font-weight: 800;
            text-transform: uppercase;
        }
        .cd-sheet-step {
            margin: 0;
            font-size: 14px;
            font-weight: 700;
            opacity: 0.9;
        }
    }

    .cd-sheet-items {
        padding: 8px 10px 4px;

        :global(.items) {
            margin-top: 0;
            background: rgba(255, 255, 255, 0.28);
        }
        :global(.items .mask) {
            height: auto;
            max-height: 46vh;
        }
        :global(.item) {
            min-height: 84px;
            height: auto;
            padding: 6px 8px;
        }
        :global(.item .item-icon) {
            width: 72px;
            height: 72px;
            max-width: 72px;
            max-height: 72px;
        }
        :global(.item .item-title) {
            font-size: 16px;
            padding-left: 14px;
            padding-right: 14px;
            white-space: normal;
            line-height: 1.2;
        }
        :global(.item .item-amount) {
            --amt-base: 26px;
        }
        :global(.item .greenCheckmark) {
            display: none;
        }
    }

    .cd-sheet-cta {
        width: 100%;
        min-height: 50px;
        border: none;
        border-radius: 12px;
        color: #0f3a06;
        background: radial-gradient(ellipse at center, $claim-green-1 10%, $claim-green-2 100%);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3), inset 0 2px 0 0 rgba(255, 255, 255, 0.45);
        font-family: var(--font-family-sans-condensed);
        font-size: 20px;
        font-weight: 800;
        cursor: pointer;

        &:disabled {
            opacity: 0.6;
        }
    }

    .cd-sheet-note {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin: 0;
        min-height: 44px;
        color: #ffe6a8;
        font-size: 15px;
        font-weight: 700;

        :global(.cd-lock) {
            position: static;
            width: 26px;
        }
    }
</style>
